/**
 * SKC Processing Progress — Shared Component
 *
 * Self-contained panel showing live menu processing progress.
 * Handles its own Socket.IO subscription, DB hydration, and auto-cleanup.
 *
 * Usage:
 *   const progress = useVaperiteProcessingProgress();
 *   <VaperiteProcessingProgressPanel {...progress} />
 *
 * Or with callbacks for when processing completes:
 *   const progress = useVaperiteProcessingProgress({ onComplete: () => refetchData() });
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Stack,
  Collapse,
  Tooltip,
  LinearProgress,
  alpha,
  keyframes,
} from '@mui/material';
import {
  SyncAlt as SyncIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  ImageSearch as ImageSearchIcon,
} from '@mui/icons-material';
import { useSocketIO, type VaperiteProcessingProgressEvent } from '../providers/SocketIOProvider';
import authInstance from '../services/httpClient';
import { useAdminTheme } from '../admin-theme';
import { stagingApiBase, stagingOrg } from '../pages/vaperite/stagingOrg';

// ============================================================================
// Types
// ============================================================================

export interface ProcessingPayloadProgress {
  payloadId: string;
  storeName: string | null;
  progress: VaperiteProcessingProgressEvent['progress'];
  receivedAt: number;
}

interface UseVaperiteProcessingProgressOptions {
  /** Called when any payload finishes processing (completed or failed). */
  onComplete?: () => void;
}

export interface ImageCompareStats {
  queued: number;
  active: number;
  completed: number;
  failed: number;
}

export interface VaperiteProcessingProgressState {
  payloads: Map<string, ProcessingPayloadProgress>;
  expanded: boolean;
  onToggle: () => void;
  /** Inject a synthetic "started" entry so the panel appears immediately (e.g. after rerun). */
  addPayload: (payloadId: string, storeName: string | null) => void;
  /** Image hash compare job stats (polled after menu processing). */
  imageCompareStats: ImageCompareStats | null;
}

// ============================================================================
// Hook — manages hydration, Socket.IO subscription, auto-cleanup
// ============================================================================

export function useVaperiteProcessingProgress(
  options?: UseVaperiteProcessingProgressOptions,
): VaperiteProcessingProgressState {
  const [payloads, setPayloads] = useState<Map<string, ProcessingPayloadProgress>>(new Map());
  const [expanded, setExpanded] = useState(true);
  const {
    subscribeSkcProcessing,
    unsubscribeSkcProcessing,
    onVaperiteProcessingProgress,
    isConnected: socketConnected,
  } = useSocketIO();

  // Stable ref for the onComplete callback
  const onCompleteRef = useRef(options?.onComplete);
  onCompleteRef.current = options?.onComplete;

  // Fetch processing state from DB (used for hydration + polling fallback)
  const fetchProcessingState = useCallback(async () => {
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/payloads/processing`);
      if (data.success && data.payloads?.length > 0) {
        setPayloads((prev) => {
          const next = new Map(prev);
          let changed = false;
          for (const p of data.payloads) {
            if (!p.progress || !p.progress.startedAt || !p.progress.steps) continue;
            const existing = prev.get(p.payloadId);
            // Only update if progress actually changed (avoids unnecessary re-renders)
            const stepsChanged = !existing || (existing.progress.steps || []).some(
              (s, i) => s.status !== (p.progress.steps || [])[i]?.status,
            );
            if (
              !existing ||
              stepsChanged ||
              existing.progress.stepIndex !== p.progress.stepIndex ||
              existing.progress.itemsProcessed !== p.progress.itemsProcessed ||
              !!existing.progress.completedAt !== !!p.progress.completedAt
            ) {
              next.set(p.payloadId, {
                payloadId: p.payloadId,
                storeName: p.storeName,
                progress: p.progress,
                receivedAt: Date.now(),
              });
              changed = true;

              // Fire onComplete when a payload finishes
              if (p.progress.completedAt && !existing?.progress.completedAt) {
                onCompleteRef.current?.();
              }
            }
          }
          return changed ? next : prev;
        });
      } else if (data.success) {
        // Backend says nothing is processing — clear any stale entries from state
        // (keeps completed entries for 30s auto-cleanup, but removes ghost entries immediately)
        setPayloads((prev) => {
          if (prev.size === 0) return prev;
          const next = new Map<string, ProcessingPayloadProgress>();
          for (const [id, p] of Array.from(prev)) {
            // Keep recently completed entries (auto-cleanup handles them after 30s)
            if (p.progress.completedAt) next.set(id, p);
          }
          return next.size !== prev.size ? next : prev;
        });
      }
    } catch {
      // Non-critical
    }
  }, []);

  // Hydrate from DB on mount
  useEffect(() => {
    fetchProcessingState();
  }, [fetchProcessingState]);

  // Poll for progress updates (fallback when Socket.IO is unavailable)
  // Fast poll (3s) when active payloads exist, slow poll (10s) to detect new ones
  const hasActivePayloads = Array.from(payloads.values()).some((p) => !p.progress.completedAt);

  useEffect(() => {
    const pollInterval = hasActivePayloads ? 3000 : 10000;
    const interval = setInterval(fetchProcessingState, pollInterval);
    return () => clearInterval(interval);
  }, [fetchProcessingState, hasActivePayloads]);

  // Subscribe to Socket.IO room (instant updates when available)
  useEffect(() => {
    if (!socketConnected) return;
    subscribeSkcProcessing();
    return () => unsubscribeSkcProcessing();
  }, [socketConnected, subscribeSkcProcessing, unsubscribeSkcProcessing]);

  // Listen for Socket.IO progress events (supplements polling with instant updates)
  useEffect(() => {
    const unsub = onVaperiteProcessingProgress((event: VaperiteProcessingProgressEvent) => {
      // One socket room serves both pipelines; keep only this page's org.
      if (event.org && event.org !== stagingOrg()) return;
      setPayloads((prev) => {
        const next = new Map(prev);
        next.set(event.payloadId, {
          payloadId: event.payloadId,
          storeName: event.storeName,
          progress: event.progress,
          receivedAt: Date.now(),
        });
        return next;
      });

      if (event.progress.completedAt) {
        onCompleteRef.current?.();
      }
    });
    return unsub;
  }, [onVaperiteProcessingProgress]);

  // ---- Image compare job stats (poll independently of payloads) ----
  const [imageCompareStats, setImageCompareStats] = useState<ImageCompareStats | null>(null);
  const imageHashingActive = imageCompareStats
    ? imageCompareStats.queued > 0 || imageCompareStats.active > 0
    : false;

  const fetchImageStats = useCallback(async () => {
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/image-compare-stats`);
      if (data.success) {
        setImageCompareStats({
          queued: data.queued,
          active: data.active,
          completed: data.completed,
          failed: data.failed,
        });
      }
    } catch {
      // Non-critical
    }
  }, []);

  // Check image stats on mount (catches hashing that started before page load)
  useEffect(() => {
    fetchImageStats();
  }, [fetchImageStats]);

  // Poll image stats when a payload just completed OR when hashing is active.
  // Self-sustaining: once hashing is detected, keeps polling until all jobs finish.
  const hasDonePayload = Array.from(payloads.values()).some((p) => !!p.progress.completedAt);
  const shouldPollImages = hasDonePayload || imageHashingActive;

  useEffect(() => {
    if (!shouldPollImages) return;
    const interval = setInterval(fetchImageStats, 5000);
    return () => clearInterval(interval);
  }, [shouldPollImages, fetchImageStats]);

  // Auto-remove completed/failed payloads after 30s — but NOT while image hashing is active
  useEffect(() => {
    if (payloads.size === 0) return;
    const interval = setInterval(() => {
      if (imageHashingActive) return; // Keep the panel visible while hashing
      const now = Date.now();
      setPayloads((prev) => {
        let changed = false;
        const next = new Map(prev);
        for (const [id, p] of Array.from(next)) {
          if (p.progress.completedAt && now - p.receivedAt > 30_000) {
            next.delete(id);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [payloads.size, imageHashingActive]);

  const onToggle = useCallback(() => setExpanded((e) => !e), []);

  /** Inject a synthetic "started" payload so the panel appears immediately. */
  const addPayload = useCallback((payloadId: string, storeName: string | null) => {
    setPayloads((prev) => {
      const next = new Map(prev);
      next.set(payloadId, {
        payloadId,
        storeName,
        progress: {
          currentStep: 'Starting...',
          stepIndex: 0,
          totalSteps: 8,
          itemsProcessed: 0,
          totalItems: 0,
          steps: [
            'Validating payload',
            'Looking up store',
            'Building menu mappings',
            'Grouping items by SKU',
            'Loading existing products',
            'Classifying item changes',
            'Refreshing stock views',
            'Refreshing category views',
          ].map((name) => ({ name, status: 'pending' as const })),
          startedAt: new Date().toISOString(),
        },
        receivedAt: Date.now(),
      });
      return next;
    });
    setExpanded(true);
  }, []);

  return { payloads, expanded, onToggle, addPayload, imageCompareStats };
}

// ============================================================================
// Animations
// ============================================================================

const progressPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

// ============================================================================
// Panel Component
// ============================================================================

export function VaperiteProcessingProgressPanel({
  payloads,
  expanded,
  onToggle,
  imageCompareStats,
}: VaperiteProcessingProgressState) {
  const { colors } = useAdminTheme();
  const imageHashingActive = imageCompareStats
    ? imageCompareStats.queued > 0 || imageCompareStats.active > 0
    : false;

  // Elapsed time ticker
  const [, setTick] = useState(0);
  useEffect(() => {
    if (payloads.size === 0) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [payloads.size]);

  if (payloads.size === 0 && !imageHashingActive) return null;

  const activeCount = Array.from(payloads.values()).filter(
    (p) => !p.progress.completedAt,
  ).length;
  const allDone = activeCount === 0;

  return (
    <Box
      sx={{
        mt: 2,
        mx: 0,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(allDone ? colors.success : colors.info, 0.3)}`,
        bgcolor: alpha(colors.bgCard, 0.8),
        backdropFilter: 'blur(12px)',
        transition: 'border-color 0.3s',
      }}
    >
      {/* Header */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          cursor: 'pointer',
          borderBottom: expanded ? `1px solid ${colors.border}` : 'none',
          '&:hover': { bgcolor: alpha(colors.bgElevated, 0.5) },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <SyncIcon
            sx={{
              fontSize: 18,
              color: allDone ? colors.success : colors.info,
              animation: allDone ? 'none' : 'spin 2s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
          <Typography
            variant="subtitle2"
            sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: '0.8rem' }}
          >
            Menu Processing
          </Typography>
          <Chip
            label={allDone ? 'Done' : `${activeCount} active`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 700,
              bgcolor: alpha(allDone ? colors.success : colors.info, 0.15),
              color: allDone ? colors.success : colors.info,
              border: `1px solid ${alpha(allDone ? colors.success : colors.info, 0.3)}`,
            }}
          />
          {payloads.size > 1 && (
            <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
              ({payloads.size} total)
            </Typography>
          )}
        </Stack>
        <IconButton size="small" sx={{ color: colors.textMuted }}>
          {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Body */}
      <Collapse in={expanded}>
        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {Array.from(payloads.values()).map((p) => (
            <ProgressCard key={p.payloadId} payload={p} />
          ))}
        </Box>

        {/* Image hash compare progress */}
        {imageCompareStats && (imageCompareStats.queued > 0 || imageCompareStats.active > 0 || imageCompareStats.completed > 0 || imageCompareStats.failed > 0) && (
          <ImageHashingCard stats={imageCompareStats} />
        )}
      </Collapse>
    </Box>
  );
}

// ============================================================================
// Progress Card (individual payload)
// ============================================================================

function ProgressCard({ payload }: { payload: ProcessingPayloadProgress }) {
  const { colors } = useAdminTheme();
  const { progress, storeName } = payload;
  const isDone = !!progress.completedAt;
  const isFailed = !!progress.error;
  const accentColor = isFailed
    ? colors.error
    : isDone
      ? colors.success
      : colors.info;

  // Elapsed time
  const elapsed = Math.floor(
    ((isDone && progress.completedAt ? new Date(progress.completedAt).getTime() : Date.now()) -
      new Date(progress.startedAt).getTime()) /
      1000,
  );
  const elapsedStr =
    elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;

  // Overall progress: step-based with sub-progress in step 5
  const baseProgress = progress.totalSteps > 0
    ? (progress.stepIndex / progress.totalSteps) * 100
    : 0;
  const itemSubProgress =
    progress.stepIndex === 5 && progress.totalItems > 0 && progress.totalSteps > 0
      ? ((progress.itemsProcessed / progress.totalItems) * (1 / progress.totalSteps)) * 100
      : 0;
  const progressValue = isDone ? 100 : Math.min(baseProgress + itemSubProgress, 99);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${colors.border}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* Store name + elapsed */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
        <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.78rem' }}>
          {storeName || 'Unknown store'}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
          {isDone ? `Completed in ${elapsedStr}` : `Started ${elapsedStr} ago`}
        </Typography>
      </Stack>

      {/* Progress bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{
            flex: 1,
            height: 6,
            borderRadius: 3,
            bgcolor: alpha(accentColor, 0.1),
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              bgcolor: accentColor,
              transition: 'transform 0.5s ease',
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{ color: colors.textMuted, fontSize: '0.65rem', minWidth: 55, textAlign: 'right' }}
        >
          Step {Math.min(progress.stepIndex + 1, progress.totalSteps)}/{progress.totalSteps}
        </Typography>
      </Box>

      {/* Current step label */}
      {!isDone && progress.stepIndex === 5 && progress.totalItems > 0 && (
        <Typography variant="caption" sx={{ color: accentColor, fontSize: '0.65rem', mb: 0.5, display: 'block' }}>
          {progress.currentStep}: {progress.itemsProcessed} / {progress.totalItems} items
        </Typography>
      )}

      {/* Error message */}
      {isFailed && (
        <Typography variant="caption" sx={{ color: colors.error, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
          Error: {progress.error}
        </Typography>
      )}

      {/* Result summary */}
      {isDone && progress.result && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: colors.success, fontSize: '0.62rem' }}>
            {progress.result.processedImmediately} immediate
          </Typography>
          <Typography variant="caption" sx={{ color: colors.warning, fontSize: '0.62rem' }}>
            {progress.result.stagedForApproval} staged
          </Typography>
          {progress.result.errors > 0 && (
            <Typography variant="caption" sx={{ color: colors.error, fontSize: '0.62rem' }}>
              {progress.result.errors} errors
            </Typography>
          )}
        </Stack>
      )}

      {/* Step list (dots) */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {(progress.steps || []).map((step, idx) => {
          const dotColor =
            step.status === 'done'
              ? colors.success
              : step.status === 'running'
                ? colors.info
                : step.status === 'skipped'
                  ? colors.textMuted
                  : alpha(colors.textMuted, 0.3);

          return (
            <Tooltip
              key={idx}
              title={`${step.name}${step.detail ? ` — ${step.detail}` : ''}${step.status === 'skipped' ? ' (skipped)' : ''}`}
              arrow
              placement="top"
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: dotColor,
                  transition: 'all 0.3s',
                  animation:
                    step.status === 'running'
                      ? `${progressPulse} 1.5s ease-in-out infinite`
                      : 'none',
                  cursor: 'default',
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

// ============================================================================
// Image Hashing Card
// ============================================================================

function ImageHashingCard({ stats }: { stats: ImageCompareStats }) {
  const { colors } = useAdminTheme();
  const total = stats.queued + stats.active + stats.completed + stats.failed;
  const processed = stats.completed + stats.failed;
  const progressValue = total > 0 ? (processed / total) * 100 : 0;
  const isRunning = stats.queued > 0 || stats.active > 0;
  const accentColor = isRunning ? colors.info : colors.success;

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${colors.border}`,
      }}
    >
      {/* Header row */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <ImageSearchIcon
            sx={{
              fontSize: 16,
              color: accentColor,
              animation: stats.active > 0 ? 'spin 2s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
          <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.78rem' }}>
            Image Hashing
          </Typography>
        </Stack>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
          {isRunning ? `${processed} / ${total}` : `${total} images`}
        </Typography>
      </Stack>

      {/* Progress bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{
            flex: 1,
            height: 6,
            borderRadius: 3,
            bgcolor: alpha(accentColor, 0.1),
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              bgcolor: accentColor,
              transition: 'transform 0.5s ease',
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{ color: colors.textMuted, fontSize: '0.65rem', minWidth: 35, textAlign: 'right' }}
        >
          {Math.round(progressValue)}%
        </Typography>
      </Box>

      {/* Stat chips */}
      <Stack direction="row" spacing={1.5}>
        {stats.completed > 0 && (
          <Typography variant="caption" sx={{ color: colors.success, fontSize: '0.62rem' }}>
            {stats.completed} done
          </Typography>
        )}
        {stats.active > 0 && (
          <Typography variant="caption" sx={{ color: colors.info, fontSize: '0.62rem' }}>
            {stats.active} running
          </Typography>
        )}
        {stats.queued > 0 && (
          <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.62rem' }}>
            {stats.queued} queued
          </Typography>
        )}
        {stats.failed > 0 && (
          <Typography variant="caption" sx={{ color: colors.error, fontSize: '0.62rem' }}>
            {stats.failed} failed
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
